import {
  getMediaTypeByExt,
  Media,
  MediaQuestionConfig,
  MediaResponseState,
  MediaStatus,
  MediaType,
  OutboxService,
  useFindMedia,
  useGrippContext,
  useLocalCapture,
} from '@gripp/shared-logic';
import { useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { Text } from 'react-native-paper';
import { tokens } from '../../../../crop-circle/tokens';
import { isIdpSourceQuestion } from '../../admin/idpQuestions';
import { useFormContext } from '../../../../forms';
import { HorizontalMediaGallery } from '../../../../media';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';
import { themedStyleSheet } from '../../../../../themes/themedStyleSheet';

const i18nPrefix = 'routines.workflow.execution.media';

/**
 * Answers that point at photos the outbox still holds on this device (a
 * reopened draft whose photos have not uploaded yet). They have no server
 * record, or one whose file is not uploaded, so they are shown from the file.
 */
const mediaOnDevice = (
  mediaIds: readonly string[],
  outbox: OutboxService | undefined
): Media[] =>
  outbox
    ? mediaIds.flatMap((id) => {
        const url = outbox.localMediaUri(id);
        if (!url) return [];
        const extension = url.split('.').pop() ?? '';
        return [
          {
            id,
            name: `${id}.${extension}`,
            extension,
            type: getMediaTypeByExt(extension),
            status: MediaStatus.Pending,
            url,
          },
        ];
      })
    : [];

export const MediaQuestionExecutionForm: RoutineQuestionExecutionForm<
  MediaQuestionConfig,
  MediaResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    MediaQuestionConfig,
    MediaResponseState
  >
) => {
  const { t } = useTranslation();
  const { mediaPickerComponent: MediaPicker, outbox } = useGrippContext();
  const fieldName = `responses.${props.responseIndex}.mediaIds`;
  const { control } = useFormContext();
  const localCapture = useLocalCapture();
  // Without the form's registry a local id could never reach the outbox.
  const captureLocally = !!props.captureLocally && !!localCapture;

  // Mirrors mediaQuestionAdminForm's `isLockedIdpSource`: `isIdpSource` survives
  // an IDP off-toggle, so this question is only actually image-locked/required
  // while the routine's IDP toggle is on too (plan finding #13).
  const isLockedIdpSource =
    !!props.idpEnabled && isIdpSourceQuestion(props.config);

  control.register(fieldName, {
    value: [],
    validate: (value: string[]) =>
      !isLockedIdpSource || (value?.length ?? 0) > 0,
  });
  const { append, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  const mediaIds: string[] = useWatch({ control, name: fieldName }) ?? [];

  const existingMedia = useFindMedia({
    filter: { id: { in: mediaIds.length > 0 ? mediaIds : undefined } },
  });
  const deviceMedia = useMemo(
    () => mediaOnDevice(mediaIds, outbox),
    [mediaIds, outbox]
  );

  const onMediaAdded = (mediaItem: Media) => {
    if (captureLocally) {
      localCapture?.register({
        id: mediaItem.id,
        name: mediaItem.name ?? '',
        extension: mediaItem.extension,
      });
    }
    append(mediaItem.id);
  };

  const onMediaDeleted = (mediaId: string) => {
    const index = mediaIds.indexOf(mediaId);
    if (index >= 0) remove(index);
  };

  return (
    <>
      <HorizontalMediaGallery
        media={existingMedia}
        deviceMedia={deviceMedia}
        onMediaAdded={onMediaAdded}
        mediaPicker={MediaPicker}
        acceptedMediaTypes={
          isLockedIdpSource
            ? [MediaType.Image]
            : [MediaType.Image, MediaType.Video]
        }
        onMediaDeleted={onMediaDeleted}
        canEditMedia={true}
        // The IDP source is a single-document capture: one image, then the add
        // affordance goes away (deleting it brings it back). Both platform
        // pickers are single-select, so the count can only grow one at a time.
        canAddMedia={!isLockedIdpSource || mediaIds.length === 0}
        onUploadingChanged={props.toggleProcessing}
        captureLocally={captureLocally}
        workspaceId={props.workspaceAsset.workspace.id}
        pickerMarginTop={0}
        // Web only: camera mode renders <input capture="environment">, which
        // makes phone browsers open the camera with no library option. Picker
        // mode omits `capture`, so the browser offers camera AND library.
        // Native stays in camera mode — its camera screen has a gallery button.
        pickerMode={Platform.OS === 'web' ? 'picker' : 'camera'}
      />
      {isLockedIdpSource && mediaIds.length === 0 && (
        <Text style={styles.hint}>{t(`${i18nPrefix}.idpImageRequired`)}</Text>
      )}
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  hint: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginTop: tokens.spacing.xs,
  },
}));
